import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Button,
} from "@mui/material";
import Paging from "./Paging";
import { fetchWithToken } from "../api/apiutils";
import LogDetailsModal from "./LogDetailsModal";
import { ErrorOutline } from "@mui/icons-material";
import MarketProfileModal from "./MarketProfileModal";

const ListLogAnalysisTable = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMarketModalOpen, setIsMarketModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const fetchData = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithToken(
        `/api/lots/analysis?page=${page}&pageLimit=${rowsPerPage}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const result = await response.json();
      setData(result.data || []);
      setTotalRecords(result.all || 0);
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / rowsPerPage);
  }, [totalRecords, rowsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleViewLogsClick = useCallback((id) => {
    setSelectedAnalysisId(id);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAnalysisId(null);
  };

  const transformLogs = (logs) => {
    const logMap = {};
    logs.forEach((log) => {
      logMap[log.code] = log;
    });
    return logMap;
  };

  const renderLogCell = (log) => {
    if (!log) return "Нет данных";

    if (log.isException) {
      return <ErrorOutline color="error" />;
    }

    const startDate = log.dateOfStart
      ? new Date(log.dateOfStart).toLocaleString("ru-RU", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "N/A";
    const endDate = log.dateOfEnd
      ? new Date(log.dateOfEnd).toLocaleString("ru-RU", {
          dateStyle: "short",
          timeStyle: "short",
        })
      : "N/A";

    return (
      <div>
        <div>Начало: {startDate}</div>
        <div>Окончание: {endDate}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  const renderMarketProfile = (marketProfile, riskProfile) => {
    if (!marketProfile) return "-";

    return (
      <div>
        <div>
          <b>marketKey:</b> {marketProfile.marketKey}
        </div>
        <div>
          <b>marketProfile productIdentity category:</b>{" "}
          {marketProfile.productIdentity?.category}
        </div>
        <div>
          <b>marketProfile productIdentity type:</b>{" "}
          {marketProfile.productIdentity?.type}
        </div>
        <div>
          <b>marketProfile productIdentity confidence:</b>{" "}
          {marketProfile.productIdentity?.confidence === "low"
            ? "Низкая"
            : marketProfile.productIdentity?.confidence === "medium"
              ? "Средняя"
              : "Высокая"}
        </div>
        <div>
          <b>marketProfile pricingParams:</b>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {marketProfile.pricingParams?.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
        <div>
          <b>riskProfile confirmedRisks:</b>{" "}
          {riskProfile?.confirmedRisks?.length
            ? riskProfile.confirmedRisks.length
            : "нет"}
        </div>
        {riskProfile.confirmedRisks.length > 0 && (
          <div>
            <b>riskProfile confirmedRisks:</b>{" "}
            {riskProfile?.confirmedRisks.map((risk) => (
              <div>{risk}</div>
            ))}
          </div>
        )}{" "}
      </div>
    );
  };

  const handleMarketProfile = (marketProfile) => {
    setSelectedProfile(marketProfile);
    setIsMarketModalOpen(true);
  };

  const handleProfileModalClose = () => {
    setIsMarketModalOpen(false);
    setSelectedProfile(null);
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Наименование (Лот)</TableCell>
              <TableCell>Дата Анализа</TableCell>
              <TableCell>Номер лота</TableCell>
              <TableCell>Тип лота</TableCell>
              {/* <TableCell>Техническая спецификация</TableCell> */}
              <TableCell>Профиль рынка</TableCell>
              <TableCell>Анализ ТС</TableCell>
              <TableCell>Подбор товара</TableCell>
              <TableCell>Запланированные лоты</TableCell>
              <TableCell>Анализ конкурентов</TableCell>
              <TableCell>Результаты аналогичных лотов</TableCell>
              <TableCell>Логи</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => {
                const logsMap = transformLogs(item.logs || []);

                return (
                  <TableRow key={item.id || index}>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{item.name || "N/A"}</TableCell>
                    <TableCell>
                      {item.dateOfCreation
                        ? new Date(item.dateOfCreation).toLocaleString(
                            "ru-RU",
                            { dateStyle: "short", timeStyle: "short" },
                          )
                        : "N/A"}
                    </TableCell>
                    <TableCell>{item.number || "N/A"}</TableCell>
                    <TableCell>{item.subject || "N/A"}</TableCell>
                    <TableCell>
                      {/* {renderMarketProfile(
                        item.marketProfile,
                        item.riskProfile,
                      )} */}
                      {item.marketProfile !== null &&
                      item.riskProfile !== null ? (
                        <Button onClick={() => handleMarketProfile(item)}>
                          Посмотреть профиль рынка
                        </Button>
                      ) : (
                        "Нет данных"
                      )}
                    </TableCell>
                    <TableCell>
                      {item.hasSummary} {renderLogCell(logsMap["Summary"])}
                    </TableCell>
                    <TableCell>
                      {item.webAnalysisState}{" "}
                      {renderLogCell(logsMap["ProductSelection"])}
                    </TableCell>
                    <TableCell>
                      {item.plannedLots} {renderLogCell(logsMap["PlannedLots"])}
                    </TableCell>
                    <TableCell>
                      {item.competitors} {renderLogCell(logsMap["Competitors"])}
                    </TableCell>
                    <TableCell>
                      {item.similarLots} {renderLogCell(logsMap["SimilarLots"])}
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleViewLogsClick(item.id)}>
                        Посмотреть логи
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Paging
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      <LogDetailsModal
        open={isModalOpen}
        onClose={handleModalClose}
        analysisId={selectedAnalysisId}
      />

      <MarketProfileModal
        open={isMarketModalOpen}
        onClose={handleProfileModalClose}
        profile={selectedProfile}
      />
    </Box>
  );
};

export default ListLogAnalysisTable;
